import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronUp,
  MessageSquare,
  PhoneIncoming,
  PhoneOutgoing,
  Reply as ReplyIcon,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatPhoneNumber } from '@/utils';
import { getEffectiveOutcome } from '@/utils/callUtils';
import { resolveOutcomeMeta } from '@/services/dialer/callOutcomeLabels';
import { formatDate } from '@/i18n/locale';
import { recordingPlaybackUrl } from '@/services/dialer/recordingUrl';
import { buildActivityFeed, type ActivityEntry, type CallLike, type SmsLike } from '@/services/dialer/activityFeed';

/**
 * Le fil d'activite d'un contact : une carte par appel et par SMS.
 *
 * L'onglet n'affichait qu'un etat vide, quelle que soit l'activite du
 * contact. Chaque carte dit en une ligne ce qui s'est passe (appel entrant,
 * appel sortant, SMS), avec le resultat en pastille, les deux numeros et la
 * date. Un appel se deplie sur sa duree, son resume et sa transcription ;
 * un SMS montre son texte et propose d'y repondre.
 */

export interface ActivityTimelineProps {
  calls: CallLike[];
  sms: SmsLike[];
  prospectPhone: string;
  /** Le numero par lequel nous joignons ce contact. */
  ourNumber?: string;
  /** Ouvre l'onglet SMS avec la reponse amorcee. */
  onReply?: (entry: ActivityEntry) => void;
}

/** « 30 sept. » : la colonne de gauche de la frise. */
function jour(valeur: string): string {
  if (!valeur) return '';
  return formatDate(valeur, { day: '2-digit', month: 'short' });
}

/** « 14:12 » : sous le jour, sur la meme colonne. */
function heure(valeur: string): string {
  if (!valeur) return '';
  return formatDate(valeur, { hour: '2-digit', minute: '2-digit' });
}

function duree(secondes?: number | null): string {
  const total = Math.max(0, Math.round(secondes || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export function ActivityTimeline({ calls, sms, prospectPhone, ourNumber = '', onReply }: ActivityTimelineProps) {
  const { t } = useTranslation(['dialer', 'common']);
  const [depliees, setDepliees] = useState<Set<string>>(new Set());

  const fil = buildActivityFeed(calls, sms, prospectPhone, ourNumber);

  const basculer = (id: string) => {
    setDepliees((courant) => {
      const suivant = new Set(courant);
      if (suivant.has(id)) suivant.delete(id); else suivant.add(id);
      return suivant;
    });
  };

  if (fil.length === 0) {
    return (
      <div className="text-center py-12">
        <MessageSquare className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">{t('dialer:prospectPanel.activity.empty')}</p>
      </div>
    );
  }

  return (
    /* 🚨 Kevin, 2026-09-30 : une frise verticale. La date et l'heure tiennent
       la colonne de gauche, avec le point et le trait qui relie les
       evenements ; le contenu occupe la droite. Le bouton « tout deplier » a
       disparu : il agissait sur tout le fil d'un coup, ce qui n'est utile a
       personne, et chaque carte se deplie deja d'un clic. */
    <div className="relative">
      {/* Le trait de la frise, derriere les points. */}
      <div className="absolute left-[4.75rem] top-2 bottom-2 w-px bg-border" aria-hidden="true" />

      <ol className="space-y-4">
      {fil.map((entree) => {
        const ouverte = depliees.has(entree.id);
        const estAppel = entree.kind === 'call';
        const Icone = estAppel
          ? (entree.direction === 'inbound' ? PhoneIncoming : PhoneOutgoing)
          : MessageSquare;
        const titre = estAppel
          ? t(`dialer:prospectPanel.activity.${entree.direction}Call`)
          : 'SMS';

        return (
          <li key={entree.id} className="flex gap-3">
            {/* La colonne de gauche : quand. Largeur fixe pour que tous les
                points soient alignes sur le trait. */}
            <div className="w-16 shrink-0 pt-3 text-right">
              <div className="text-xs font-medium text-foreground tabular-nums">{jour(entree.at)}</div>
              <div className="text-[11px] text-muted-foreground tabular-nums">{heure(entree.at)}</div>
            </div>

            {/* Le point sur le trait. */}
            <div className="relative w-3 shrink-0" aria-hidden="true">
              <span className={`absolute left-1/2 top-[1.15rem] -translate-x-1/2 w-2.5 h-2.5 rounded-full ring-4 ring-background ${
                estAppel ? 'bg-foreground/60' : 'bg-foreground/30'
              }`} />
            </div>

            <article
              data-testid="activite"
              className="flex-1 min-w-0 rounded-xl border border-border bg-card px-4 py-3"
            >
            <header className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Icone className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="text-sm font-semibold text-foreground">{titre}</span>
                {estAppel ? (
                  (() => {
                    const meta = resolveOutcomeMeta(
                      getEffectiveOutcome(entree.call),
                      entree.call?.call_status,
                      t,
                    );
                    return <Badge className={meta.tone}>{meta.label}</Badge>;
                  })()
                ) : (
                  <Badge variant="secondary">
                    {t(`dialer:prospectPanel.activity.${entree.direction}`)}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {estAppel && (
                  <button
                    type="button"
                    aria-label={t('dialer:prospectPanel.activity.toggleDetail')}
                    onClick={() => basculer(entree.id)}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {ouverte ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </header>

            <p className="mt-1 text-xs text-muted-foreground">
              {formatPhoneNumber(entree.from)} → {formatPhoneNumber(entree.to)}
            </p>

            {!estAppel && (
              <>
                <p className="mt-3 text-sm text-foreground whitespace-pre-wrap">{entree.sms?.message}</p>
                {onReply && entree.direction === 'inbound' && (
                  <div className="mt-3 flex justify-end">
                    <Button type="button" size="sm" onClick={() => onReply(entree)}>
                      <ReplyIcon className="w-3.5 h-3.5 mr-1.5" />
                      {t('dialer:prospectPanel.activity.reply')}
                    </Button>
                  </div>
                )}
              </>
            )}

            {estAppel && ouverte && (
              <dl className="mt-3 space-y-2 border-t border-border pt-3 text-sm">
                <div className="flex gap-2">
                  <dt className="text-muted-foreground shrink-0">{t('dialer:prospectPanel.activity.duration')}</dt>
                  <dd className="text-foreground">{duree(entree.call?.duration_seconds)}</dd>
                </div>
                {/* Jamais l'adresse Twilio nue : elle demande une
                    authentification, et le navigateur ouvre sa fenetre
                    « Se connecter a Twilio » par-dessus la fiche. */}
                {entree.call?.recording_url ? (
                  <audio src={recordingPlaybackUrl(entree.call.recording_url)} controls className="h-8 w-full" />
                ) : null}
                {entree.call?.ai_summary ? (
                  <div>
                    <dt className="text-muted-foreground">{t('dialer:prospectPanel.activity.summary')}</dt>
                    <dd className="text-foreground whitespace-pre-wrap">{String(entree.call.ai_summary)}</dd>
                  </div>
                ) : null}
                {entree.call?.transcription ? (
                  <div>
                    <dt className="text-muted-foreground">{t('dialer:prospectPanel.activity.transcript')}</dt>
                    <dd className="text-foreground whitespace-pre-wrap max-h-48 overflow-y-auto">
                      {String(entree.call.transcription)}
                    </dd>
                  </div>
                ) : null}
              </dl>
            )}
            </article>
          </li>
        );
      })}
      </ol>
    </div>
  );
}
